import {
  AlertCircle,
  ArrowDownToLine,
  ArrowLeft,
  Bot,
  Check,
  CheckCheck,
  Clock3,
  Download,
  File,
  FileAudio,
  FileImage,
  FileVideo,
  LoaderCircle,
  MessageSquareText,
  RefreshCw,
  Send,
  UserRound,
  X,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";

import {
  ApiError,
  downloadAttachment,
  getMessages,
  sendMessage,
  setConversationMode,
} from "../api";
import type { Attachment, Conversation, Message } from "../types";
import {
  contactName,
  dateKey,
  formatBytes,
  formatFlowMessage,
  formatFullDate,
  formatMessageTime,
  initials,
} from "../utils";


interface Props {
  token: string;
  conversation: Conversation | null;
  onBack: () => void;
  onConversationUpdated: (conversation: Conversation) => void;
  onConversationRead: (conversationId: number) => void;
  onMessageSent: () => void;
}

function statusIcon(message: Message) {
  if (message.direction !== "outbound") return null;
  if (message.status === "read") return <CheckCheck size={14} className="status-read" />;
  if (message.status === "delivered") return <CheckCheck size={14} />;
  if (message.status === "sent") return <Check size={14} />;
  if (message.status === "failed") return <AlertCircle size={14} className="status-failed" />;
  return <Clock3 size={12} />;
}

function attachmentIcon(attachment: Attachment) {
  if (attachment.mime_type.startsWith("image/")) return FileImage;
  if (attachment.mime_type.startsWith("audio/")) return FileAudio;
  if (attachment.mime_type.startsWith("video/")) return FileVideo;
  return File;
}

function AttachmentCard({
  attachment,
  downloading,
  onDownload,
}: {
  attachment: Attachment;
  downloading: boolean;
  onDownload: () => void;
}) {
  const Icon = attachmentIcon(attachment);
  const unavailable = attachment.download_status === "deleted";
  return (
    <button
      className="attachment-card"
      onClick={onDownload}
      disabled={downloading || unavailable}
      title={attachment.download_error || "Scarica allegato"}
    >
      <span className="attachment-icon"><Icon size={20} /></span>
      <span className="attachment-copy">
        <strong>{attachment.original_name || "Allegato WhatsApp"}</strong>
        <small>
          {formatBytes(attachment.size) || attachment.mime_type || "File"}
          {attachment.download_status === "pending" ? " · da importare" : ""}
          {attachment.download_status === "failed" ? " · riprova" : ""}
        </small>
      </span>
      <span className="attachment-action">
        {downloading ? <LoaderCircle size={18} className="spin" /> : <Download size={18} />}
      </span>
    </button>
  );
}

export default function ChatPanel({
  token,
  conversation,
  onBack,
  onConversationUpdated,
  onConversationRead,
  onMessageSent,
}: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [error, setError] = useState("");
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const messageAreaRef = useRef<HTMLElement>(null);
  const previousConversationId = useRef<number | null>(null);

  const mergeMessages = useCallback((current: Message[], incoming: Message[]) => {
    const merged = new Map<number, Message>();
    current.forEach((message) => merged.set(message.id, message));
    incoming.forEach((message) => merged.set(message.id, message));
    return [...merged.values()].sort((a, b) => a.id - b.id);
  }, []);

  const loadLatest = useCallback(async (silent = false) => {
    if (!conversation) return;
    if (!silent) setLoading(true);
    try {
      const result = await getMessages(token, conversation.id);
      setMessages((current) =>
        silent ? mergeMessages(current, result.results) : result.results,
      );
      setHasMore(result.has_more);
      setNextBeforeId(result.next_before_id);
      setError("");
      onConversationRead(conversation.id);
    } catch (caught) {
      if (!(caught instanceof DOMException && caught.name === "AbortError")) {
        setError(caught instanceof Error ? caught.message : "Errore nel caricamento dei messaggi.");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [conversation, mergeMessages, onConversationRead, token]);

  useEffect(() => {
    if (!conversation) {
      setMessages([]);
      previousConversationId.current = null;
      return;
    }
    const changed = previousConversationId.current !== conversation.id;
    previousConversationId.current = conversation.id;
    if (changed) {
      setMessages([]);
      setDraft("");
      setError("");
      setShowHandoff(false);
      void loadLatest();
    }
    const interval = window.setInterval(() => void loadLatest(true), 8000);
    return () => window.clearInterval(interval);
  }, [conversation?.id, loadLatest]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const refreshAfterPush = (event: MessageEvent) => {
      if (
        event.data?.type === "GDP_PUSH_RECEIVED" &&
        event.data?.conversationId === conversation?.id
      ) {
        void loadLatest(true);
      }
    };
    navigator.serviceWorker.addEventListener("message", refreshAfterPush);
    return () =>
      navigator.serviceWorker.removeEventListener("message", refreshAfterPush);
  }, [conversation?.id, loadLatest]);

  useEffect(() => {
    if (!showHandoff) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowHandoff(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [showHandoff]);

  useEffect(() => {
    if (!loading && messages.length) {
      const messageArea = messageAreaRef.current;
      messageArea?.scrollTo({
        top: messageArea.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [loading, messages.length, conversation?.id]);

  async function loadOlder() {
    if (!conversation || !nextBeforeId || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const result = await getMessages(token, conversation.id, nextBeforeId);
      setMessages((current) => mergeMessages(result.results, current));
      setHasMore(result.has_more);
      setNextBeforeId(result.next_before_id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Impossibile caricare i messaggi precedenti.");
    } finally {
      setLoadingOlder(false);
    }
  }

  async function changeMode(mode: "ai" | "human") {
    if (!conversation || switchingMode) return;
    setSwitchingMode(true);
    setError("");
    try {
      const updated = await setConversationMode(token, conversation.id, mode);
      onConversationUpdated(updated);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Impossibile cambiare modalità.");
    } finally {
      setSwitchingMode(false);
    }
  }

  async function handleSend(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!conversation || !text || sending) return;
    setSending(true);
    setError("");
    try {
      const message = await sendMessage(token, conversation.id, text);
      setMessages((current) => mergeMessages(current, [message]));
      setDraft("");
      onMessageSent();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Il messaggio non è stato inviato.",
      );
    } finally {
      setSending(false);
    }
  }

  async function handleDownload(attachment: Attachment) {
    setDownloadingId(attachment.id);
    setError("");
    try {
      await downloadAttachment(
        token,
        attachment.download_url,
        attachment.original_name,
      );
      await loadLatest(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Download non riuscito.");
    } finally {
      setDownloadingId(null);
    }
  }

  if (!conversation) {
    return (
      <main className="chat-empty">
        <div className="empty-chat-illustration">
          <MessageSquareText size={34} />
        </div>
        <h2>Seleziona una conversazione</h2>
        <p>Qui vedrai messaggi, allegati e stato della gestione.</p>
      </main>
    );
  }

  const name = contactName(conversation);
  const composerAvailable =
    conversation.mode === "human" && conversation.can_reply;

  return (
    <main className="chat-panel">
      <header className="chat-header">
        <button className="icon-button mobile-back" onClick={onBack} aria-label="Torna alle conversazioni">
          <ArrowLeft size={21} />
        </button>
        <div className="contact-avatar header-avatar">{initials(name)}</div>
        <div className="chat-contact">
          <strong>{name}</strong>
          <span>+{conversation.wa_id.replace(/^\+/, "")}</span>
        </div>
        <div className="mode-control">
          <div className={`mode-status ${conversation.mode}`}>
            {conversation.mode === "human" ? <UserRound size={15} /> : <Bot size={15} />}
            <span>{conversation.mode === "human" ? "Operatore" : "Assistente AI"}</span>
          </div>
          <button
            className="secondary-button"
            disabled={switchingMode}
            onClick={() =>
              void changeMode(conversation.mode === "human" ? "ai" : "human")
            }
          >
            {switchingMode ? (
              <LoaderCircle size={16} className="spin" />
            ) : conversation.mode === "human" ? (
              <Bot size={16} />
            ) : (
              <UserRound size={16} />
            )}
            <span className="secondary-button-label">
              {conversation.mode === "human" ? "Riaffida all’AI" : "Prendi in carico"}
            </span>
          </button>
        </div>
      </header>

      {conversation.handoff_summary && conversation.mode === "human" && (
        <button
          type="button"
          className="handoff-note"
          onClick={() => setShowHandoff(true)}
          title="Mostra tutto il contesto"
        >
          <span className="handoff-note-icon"><UserRound size={16} /></span>
          <span className="handoff-note-copy">
            <strong>Contesto del passaggio</strong>
            <span className="handoff-note-text">
              {conversation.handoff_summary}
            </span>
          </span>
        </button>
      )}

      {showHandoff && conversation.handoff_summary && (
        <div
          className="handoff-modal-backdrop"
          onClick={() => setShowHandoff(false)}
          role="presentation"
        >
          <div
            className="handoff-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Contesto del passaggio"
            onClick={(event) => event.stopPropagation()}
          >
            <header>
              <strong>Contesto del passaggio</strong>
              <button
                className="icon-button"
                onClick={() => setShowHandoff(false)}
                aria-label="Chiudi"
              >
                <X size={18} />
              </button>
            </header>
            <p className="handoff-modal-body">{conversation.handoff_summary}</p>
          </div>
        </div>
      )}

      <section className="message-area" ref={messageAreaRef}>
        {hasMore && (
          <button className="load-older" onClick={() => void loadOlder()} disabled={loadingOlder}>
            {loadingOlder ? <LoaderCircle size={15} className="spin" /> : <ArrowDownToLine size={15} className="rotate-180" />}
            Carica messaggi precedenti
          </button>
        )}

        {loading && messages.length === 0 && (
          <div className="message-loading">
            <LoaderCircle className="spin" size={24} />
            <span>Caricamento conversazione…</span>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="message-loading">
            <MessageSquareText size={24} />
            <span>Nessun messaggio in questa conversazione.</span>
          </div>
        )}

        {messages.map((message, index) => {
          const previous = messages[index - 1];
          const showDate = !previous || dateKey(previous.occurred_at) !== dateKey(message.occurred_at);
          const incoming = message.direction === "inbound";
          return (
            <div key={message.id}>
              {showDate && (
                <div className="date-divider">
                  <span>{formatFullDate(message.occurred_at)}</span>
                </div>
              )}
              <article className={`message-line ${incoming ? "incoming" : "outgoing"}`}>
                <div className={`message-bubble ${message.author_type}`}>
                  {!incoming && (
                    <span className="message-author">
                      {message.author_type === "operator" ? "Operatore" : message.author_type === "ai" ? "Assistente AI" : "Sistema"}
                    </span>
                  )}
                  {message.text && (
                    <p className={message.text.startsWith("[Flow]") ? "flow-message" : undefined}>
                      {formatFlowMessage(message.text)}
                    </p>
                  )}
                  {message.attachment && (
                    <AttachmentCard
                      attachment={message.attachment}
                      downloading={downloadingId === message.attachment.id}
                      onDownload={() => void handleDownload(message.attachment!)}
                    />
                  )}
                  {!message.text && !message.attachment && (
                    <p className="unsupported-message">
                      Messaggio {message.message_type}
                    </p>
                  )}
                  <footer>
                    <time>{formatMessageTime(message.occurred_at)}</time>
                    {statusIcon(message)}
                  </footer>
                </div>
              </article>
            </div>
          );
        })}
      </section>

      {error && (
        <div className="chat-error" role="alert">
          <AlertCircle size={17} />
          <span>{error}</span>
          <button onClick={() => setError("")} aria-label="Chiudi">×</button>
        </div>
      )}

      <footer className="composer-shell">
        {conversation.mode === "ai" && (
          <div className="composer-blocked">
            <Bot size={17} />
            <span>La conversazione è gestita dall’AI.</span>
            <button onClick={() => void changeMode("human")}>Prendi in carico</button>
          </div>
        )}
        {conversation.mode === "human" && !conversation.can_reply && (
          <div className="composer-blocked warning">
            <Clock3 size={17} />
            <span>Finestra di 24 ore chiusa. Serve un template approvato.</span>
          </div>
        )}
        <form className="composer" onSubmit={handleSend}>
          <textarea
            rows={1}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
            placeholder={
              composerAvailable
                ? "Scrivi un messaggio…"
                : "Invio non disponibile"
            }
            disabled={!composerAvailable || sending}
            aria-label="Messaggio"
          />
          <button
            className="send-button"
            disabled={!composerAvailable || !draft.trim() || sending}
            aria-label="Invia messaggio"
          >
            {sending ? <LoaderCircle size={20} className="spin" /> : <Send size={19} />}
          </button>
        </form>
        <p className="composer-hint">
          Invio con Invio · nuova riga con Maiusc + Invio
        </p>
      </footer>
    </main>
  );
}
